import superagent from 'superagent';
import { getLogger } from './logger';
import { makeCounters } from './httpRequestCounters';

const logger = getLogger('http-request');
const wrapWithMonitoring = makeCounters();
const DEFAULT_REQUEST_TIMEOUT = parseInt(process.env.DEFAULT_REQUEST_TIMEOUT || '0', 10) || 30_000; //30sec timeout
const DOWNLOAD_REQUEST_TIMEOUT = 60_000; //1min timeout

// #region utilities
function getCaFile() {
    return global.caFileContent;
}

function binaryParser(res: superagent.Response, fn: (err: Error | null, body: any) => void) {
    const data: Uint8Array[] = [];

    res.on('data', (chunk) => {
        data.push(chunk); // Append Buffer object
    });
    res.on('end', () => {
        fn(null, Buffer.concat(data)); // Merge the chunks and return
    });
}

function getProxy() {
    if (!('proxy' in superagent.Request.prototype) && global.SuperagentProxy) {
        // Type assertion needed due to pnpm's strict dependency resolution creating duplicate type definitions
        global.SuperagentProxy(superagent as any);
    }
    return global.proxyUri;
}

const logErrorAndRethrow = (logMsg: string, error: any, data?: object) => {
    const response = error.response;
    if (error.response instanceof superagent.Response && typeof response.toError === 'function') {
        // This will give us status, method, body text, and a stack trace that doesn't suck without internal superagent stuff
        error = error.response.toError();
        error.requestHeaders = response?.header;
        error.responseHeaders = response?.headers;
    }
    const logErrorAndRethrowStack = new Error('').stack;
    logger.error(logMsg, { ...data, error, logErrorAndRethrowStack });
    throw error;
};

function attachCaFileAndProxyToRequest(request: superagent.SuperAgentRequest, skipProxy = false) {
    const caFile = getCaFile();
    if (caFile) {
        request.ca(caFile);
    }

    const proxy = !skipProxy && getProxy();
    if (proxy) {
        request.proxy(proxy);
    }
}
// #endregion

export const deleteReq = wrapWithMonitoring(async (url: string, body: object = {}, headers: Record<string, string> = {}, timeout = DEFAULT_REQUEST_TIMEOUT) => {
    try {
        const request = superagent
            .delete(url)
            .send(body)
            .timeout(timeout)
            .set(headers);

        attachCaFileAndProxyToRequest(request);

        const res = await request;
        if (res.type === 'text/plain') {
            return res.text;
        }
        return res.body;
    } catch (err) {
        return logErrorAndRethrow('failed to delete request', err, { url });
    }
});

export const post = wrapWithMonitoring(async ({ url, body, headers, timeout, retry }: { url: string; body: object; headers?: { [key: string]: any }; timeout?: number; retry?: number }) => {
    try {
        const res = await _post(url, body, headers, timeout, retry);
        if (res.type === 'text/plain') {
            return res.text;
        }
        return res.body;
    } catch (err) {
        return logErrorAndRethrow('failed to post request', err, { url });
    }
});

async function _post(url: string, body: object, headers: Record<string, string> = {}, timeout = DEFAULT_REQUEST_TIMEOUT, retry = 0) {
    const request = superagent
        .post(url)
        .send(body)
        .timeout(timeout)
        .set(headers);

    attachCaFileAndProxyToRequest(request);

    if (retry) {
        request.retry(retry);
    }

    try {
        return await request;
    } catch (err) {
        err.url = url;
        err.originalRequestTimeout = timeout;
        err.additionalSetHeaders = headers;
        throw err;
    }
}

export interface Files { [fieldName: string]: { buffer: Buffer; fileName: string } }
export const postForm = wrapWithMonitoring(async (url: string, fields: { [fieldName: string]: any }, files: Files, headers: Record<string, string> = {}, timeout: number = DEFAULT_REQUEST_TIMEOUT) => {
    const request = superagent
        .post(url)
        .type('form')
        .timeout(timeout)
        .set(headers);

    request.field(fields);

    Object.entries(files).forEach(([fileFormName, file]) => {
        request.attach(fileFormName, file.buffer, file.fileName);
    });

    attachCaFileAndProxyToRequest(request);

    try {
        const res = await request;
        if (res.type === 'text/plain') {
            return res.text;
        }
        return res.body;
    } catch (err) {
        return logErrorAndRethrow('failed to post request', err, { url });
    }
});

async function _get(url: string, query?: string | object, headers: Record<string, string> = {}, timeout = DEFAULT_REQUEST_TIMEOUT, { isBinary = false, skipProxy = false } = {}) {
    const request = superagent
        .get(url)
        .query(query || {})
        .timeout(timeout)
        .set(headers);

    if (isBinary) {
        request.buffer(true);
    }

    attachCaFileAndProxyToRequest(request, skipProxy);

    return await request;
}

export const getText = wrapWithMonitoring(async (url: string, query?: string | object, headers?: Record<string, string>) => {
    try {
        const res = await _get(url, query, headers);
        return res.text;
    } catch (err) {
        return logErrorAndRethrow('failed to getText request', err, { url, query });
    }
});

export const postText = wrapWithMonitoring(async ({ url, body, headers, timeout, retry }: { url: string; body: object; headers?: { [key: string]: any }; timeout?: number; retry?: number }) => {
    try {
        const res = await _post(url, body, headers, timeout, retry);
        return res.text;
    } catch (err) {
        return logErrorAndRethrow('failed to post request', err, { url });
    }
});

export const get = wrapWithMonitoring(async (url: string, query?: string | object, headers?: Record<string, string>, timeout?: number, options?: { isBinary?: boolean; skipProxy?: boolean }) => {
    try {
        const res = await _get(url, query, headers, timeout, options);
        return res.body;
    } catch (err) {
        return logErrorAndRethrow('failed to get request', err, { url, query });
    }
});

export const getFullRes = wrapWithMonitoring((url: string, query?: string | object, headers?: Record<string, string>, timeout?: number) => _get(url, query, headers, timeout));

export const head = wrapWithMonitoring(async (url: string) => {
    const request = superagent
        .head(url)
        .timeout(DEFAULT_REQUEST_TIMEOUT);

    attachCaFileAndProxyToRequest(request);

    try {
        return await request;
    } catch (err) {
        return logErrorAndRethrow('failed to head request', err, { url });
    }
});

export const put = wrapWithMonitoring(async (url: string, body?: object, headers: Record<string, string> = {}, timeout: number = DEFAULT_REQUEST_TIMEOUT) => {
    const request = superagent
        .put(url)
        .send(body)
        .timeout(timeout)
        .set(headers);

    attachCaFileAndProxyToRequest(request);

    try {
        const res = await request;
        return res.body;
    } catch (err) {
        return logErrorAndRethrow('failed to put request', err, { url });
    }
});

export const download = wrapWithMonitoring(async (url: string) => {
    logger.info('start to download', { url });

    const request = superagent
        .get(url)
        .set('User-Agent', 'Testim.io')
        .timeout(DOWNLOAD_REQUEST_TIMEOUT)
        .buffer(true)
        .parse(binaryParser);

    attachCaFileAndProxyToRequest(request);

    try {
        const res = await request as Omit<superagent.Response, 'body'> & { body: Buffer };
        logger.info('finished to download', { url });
        return res;
    } catch (err) {
        return logErrorAndRethrow('failed to download', err, { url });
    }
});

export const isNetworkHealthy = wrapWithMonitoring.isNetworkHealthy;
export const didNetworkConnectivityTestFail = wrapWithMonitoring.didNetworkConnectivityTestFail;
